import React, { useState, useEffect, useRef } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import Navbar from './components/Navbar';
import Dashboard from './components/Dashboard';
import AuthModal from './components/AuthModal';
import ProtocolModal from './components/ProtocolModal';
import HistoryLogModal from './components/HistoryLogModal';
import OnboardingModal from './components/OnboardingModal';
import LandingHero from './components/LandingHero';
import ToolsHub from './components/tools/ToolsHub';
import CalendarHub from './components/calendar/CalendarHub';
import SerumHub from './components/serum/SerumHub';
import ReleaseNotesModal from './components/ReleaseNotesModal';
import { APP_VERSION } from './utils/version';
import { getTabFromPath, navigateToTab, ROUTE_TITLES } from './utils/navigation';
import { 
  addProtocol, 
  updateProtocol, 
  deleteProtocol, 
  getAuditLogs, 
  seedDefaultStack,
  SEED_PROTOCOLS,
  createCheckpoint,
  deleteCheckpoint,
  clearAllProtocols,
  restoreFromCheckpoint,
  subscribeToProtocols,
  subscribeToCheckpoints,
  subscribeToAuditLogs,
  getLocalAuditLogs,
  processAutoStatusTransitions
} from './services/protocolService';

function MainApp() {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState(() => getTabFromPath(window.location.pathname));

  // Sync document title and listen for browser back/forward history navigation
  useEffect(() => {
    const initialTab = getTabFromPath(window.location.pathname);
    if (ROUTE_TITLES[initialTab]) {
      document.title = ROUTE_TITLES[initialTab];
    }

    const handlePopState = () => {
      const tab = getTabFromPath(window.location.pathname);
      setActiveTab(tab);
      if (ROUTE_TITLES[tab]) {
        document.title = ROUTE_TITLES[tab];
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const handleSelectTab = (tabId) => {
    navigateToTab(tabId);
    setActiveTab(tabId);
  };
  const [protocols, setProtocols] = useState([]);
  const [checkpoints, setCheckpoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modals state
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [isProtocolModalOpen, setIsProtocolModalOpen] = useState(false);
  const [protocolToEdit, setProtocolToEdit] = useState(null);
  const [isDuplicate, setIsDuplicate] = useState(false);

  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [historyLogs, setHistoryLogs] = useState([]);
  const [historyProtocolName, setHistoryProtocolName] = useState('');

  // First-time Onboarding Modal state
  const [isOnboardingOpen, setIsOnboardingOpen] = useState(false);
  const [isSeedingTemplate, setIsSeedingTemplate] = useState(false);
  const [allAuditLogs, setAllAuditLogs] = useState([]);
  const [isReleaseNotesOpen, setIsReleaseNotesOpen] = useState(false);

  // Force re-subscription on tab visibility restore (mobile PWA foreground resume).
  const [refreshKey, setRefreshKey] = useState(0);

  // Pending-operation guards (double-click protection) + one-shot auto-transition guard.
  // Subscriptions remain the single source of truth; handlers never patch state manually.
  const pendingOpsRef = useRef(new Set());
  const autoTransitionRanRef = useRef(false);
  const tryBeginOp = (key) => {
    if (pendingOpsRef.current.has(key)) return false;
    pendingOpsRef.current.add(key);
    return true;
  };
  const endOp = (key) => {
    pendingOpsRef.current.delete(key);
  };

  // Subscribe to real-time protocols, checkpoints & audit logs whenever auth user changes
  useEffect(() => {
    // Reset per-user guards whenever the auth identity changes.
    pendingOpsRef.current.clear();
    autoTransitionRanRef.current = false;
    if (!currentUser) {
      setProtocols([]);
      setCheckpoints([]);
      setAllAuditLogs(getLocalAuditLogs('demo_user_123'));
      setLoading(false);
      setError(null);
      setIsOnboardingOpen(false);
      return;
    }

    setLoading(true);
    let isInitialLoad = true;
    let didTimeout = false;

    // Safety net: if Firestore onSnapshot never emits (common on mobile PWA
    // foreground resume with a stale auth token), stop spinning and offer retry.
    const LOAD_TIMEOUT_MS = 10000;
    const loadTimeout = setTimeout(() => {
      didTimeout = true;
      setLoading(false);
      setError('Protocol sync is taking longer than expected. Tap Retry to reconnect.');
    }, LOAD_TIMEOUT_MS);

    const unsubProtocols = subscribeToProtocols(
      currentUser.uid,
      (data) => {
        clearTimeout(loadTimeout);
        const seen = new Set();
        const unique = (data || []).filter(p => {
          if (!p?.id || seen.has(p.id)) return false;
          seen.add(p.id);
          return true;
        });
        setProtocols(unique);
        if (isInitialLoad || didTimeout) {
          setLoading(false);
          isInitialLoad = false;
          didTimeout = false;

          // Check if first-time user has an empty stack and has not dismissed onboarding
          const onboardedKey = `supps_peps_onboarded_${currentUser.uid}`;
          const hasOnboarded = localStorage.getItem(onboardedKey);
          if (!hasOnboarded && unique.length === 0) {
            setIsOnboardingOpen(true);
          }

          // Run auto-status transitions exactly once per user on initial load.
          // Never inside the recurring snapshot callback (feedback loop).
          if (!autoTransitionRanRef.current) {
            autoTransitionRanRef.current = true;
            const uid = currentUser.uid;
            const snapshot = unique;
            processAutoStatusTransitions(uid, snapshot).catch((err) => {
              // Allow a retry on next login if the one-shot run fails.
              autoTransitionRanRef.current = false;
              console.warn("Background auto-transition check:", err);
            });
          }
        }
        setError(null);
      },
      (err) => {
        clearTimeout(loadTimeout);
        setError(err.message || "Failed to sync protocol stack.");
        console.error("Error subscribing to protocols:", err);
        setLoading(false);
        isInitialLoad = false;
      }
    );

    const unsubCheckpoints = subscribeToCheckpoints(
      currentUser.uid,
      (cpData) => {
        const seen = new Set();
        const unique = (cpData || []).filter(cp => {
          if (!cp?.id || seen.has(cp.id)) return false;
          seen.add(cp.id);
          return true;
        });
        setCheckpoints(unique);
      },
      (err) => {
        console.warn("Error subscribing to checkpoints:", err);
      }
    );

    const unsubAuditLogs = subscribeToAuditLogs(
      currentUser.uid,
      (logs) => {
        setAllAuditLogs(logs || []);
      },
      (err) => {
        console.warn("Error subscribing to audit logs:", err);
      }
    );

    return () => {
      clearTimeout(loadTimeout);
      unsubProtocols();
      unsubCheckpoints();
      unsubAuditLogs();
    };
  }, [currentUser, refreshKey]);

  // When a mobile PWA is foregrounded after being in the background, Firestore
  // can get stuck with a stale auth token. Force a fresh subscription handshake
  // so the user doesn't stay trapped on the loading spinner.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        setRefreshKey(k => k + 1);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Handlers
  const handleOpenAddModal = () => {
    if (!currentUser) {
      setIsAuthOpen(true);
      return;
    }
    setProtocolToEdit(null);
    setIsDuplicate(false);
    setIsProtocolModalOpen(true);
  };

  const handleOpenEditModal = (protocol) => {
    setProtocolToEdit(protocol);
    setIsDuplicate(false);
    setIsProtocolModalOpen(true);
  };

  const handleOpenDuplicateModal = (protocol) => {
    if (!currentUser) {
      setIsAuthOpen(true);
      return;
    }
    setProtocolToEdit(protocol);
    setIsDuplicate(true);
    setIsProtocolModalOpen(true);
  };

  const handleSaveProtocol = async (formData, changeNotes) => {
    if (!currentUser) return;
    if (!tryBeginOp('save-protocol')) return;

    try {
      if (protocolToEdit && !isDuplicate) {
        // Update — onSnapshot subscription delivers the result.
        await updateProtocol(
          currentUser.uid, 
          protocolToEdit.id, 
          protocolToEdit, 
          formData, 
          changeNotes || 'Updated protocol specifications'
        );
      } else {
        // Add or Duplicate — onSnapshot subscription delivers the result.
        await addProtocol(currentUser.uid, formData);
      }
      setError(null);
    } catch (err) {
      setError(err.message || "Failed to save protocol.");
      console.error("Error saving protocol:", err);
      throw err;
    } finally {
      endOp('save-protocol');
    }
  };

  const handleStatusChange = async (protocol, newStatus) => {
    if (!currentUser || protocol.cycleStatus === newStatus) return;
    const opKey = `status-${protocol.id}`;
    if (!tryBeginOp(opKey)) return;
    const today = new Date().toISOString().split('T')[0];
    const updatePayload = { cycleStatus: newStatus };

    // If activating a protocol with a future start date, advance start date to today
    if (newStatus === 'Active' && protocol.startDate && protocol.startDate > today) {
      updatePayload.startDate = today;
    }

    try {
      await updateProtocol(
        currentUser.uid,
        protocol.id,
        protocol,
        updatePayload,
        `Status toggled from ${protocol.cycleStatus} to ${newStatus}`
      );
      setError(null);
    } catch (err) {
      setError(err.message || "Failed to update status.");
      console.error("Error changing status:", err);
    } finally {
      endOp(opKey);
    }
  };

  const handleDeleteProtocol = async (protocol) => {
    if (!currentUser) return;
    const opKey = `delete-${protocol.id}`;
    if (!tryBeginOp(opKey)) return;
    if (window.confirm(`Are you sure you want to delete ${protocol.name} from your stack?`)) {
      try {
        await deleteProtocol(currentUser.uid, protocol.id, protocol);
        setError(null);
      } catch (err) {
        setError(err.message || "Failed to delete protocol.");
        console.error("Error deleting protocol:", err);
      } finally {
        endOp(opKey);
      }
    } else {
      endOp(opKey);
    }
  };

  const handleViewHistory = async (protocol = null) => {
    if (!currentUser) {
      setIsAuthOpen(true);
      return;
    }
    try {
      setHistoryProtocolName(protocol ? protocol.name : '');
      const logs = await getAuditLogs(currentUser.uid, protocol ? protocol.id : null);
      setHistoryLogs(logs);
      setIsHistoryModalOpen(true);
      setError(null);
    } catch (err) {
      setError(err.message || "Failed to load history.");
      console.error("Error loading history:", err);
    }
  };

  const handleCloseOnboarding = () => {
    if (currentUser) {
      localStorage.setItem(`supps_peps_onboarded_${currentUser.uid}`, 'true');
    }
    setIsOnboardingOpen(false);
  };

  const handleLoadSampleTemplate = async () => {
    if (!currentUser) {
      setIsAuthOpen(true);
      return;
    }
    setIsSeedingTemplate(true);
    try {
      await seedDefaultStack(currentUser.uid);
      handleCloseOnboarding();
      setError(null);
    } catch (err) {
      setError(err.message || "Failed to load sample template.");
      console.error("Error loading sample template:", err);
    } finally {
      setIsSeedingTemplate(false);
    }
  };

  const handleStartBlank = () => {
    handleCloseOnboarding();
  };

  const handleSeedData = async () => {
    if (!currentUser) {
      setIsAuthOpen(true);
      return;
    }
    if (!tryBeginOp('seed')) return;
    setLoading(true);
    try {
      // Subscription (Firestore onSnapshot / local-storage-sync) delivers the seeded rows.
      await seedDefaultStack(currentUser.uid);
      setError(null);
    } catch (err) {
      setError(err.message || "Failed to seed default stack.");
      console.error("Error seeding stack:", err);
    } finally {
      setLoading(false);
      endOp('seed');
    }
  };

  const handleSaveCalculatorToProtocol = async (protocolId, notesSnippet) => {
    if (!currentUser) {
      setIsAuthOpen(true);
      return;
    }
    const opKey = `calc-${protocolId}`;
    if (!tryBeginOp(opKey)) return;
    const target = protocols.find(p => p.id === protocolId);
    if (!target) {
      endOp(opKey);
      return;
    }

    const currentNotes = target.notes ? target.notes.trim() : '';
    const updatedNotes = currentNotes ? `${currentNotes}\n\n${notesSnippet}` : notesSnippet;

    try {
      await updateProtocol(
        currentUser.uid,
        target.id,
        target,
        { notes: updatedNotes },
        'Appended peptide reconstitution calculation to notes'
      );
      setError(null);
    } catch (err) {
      setError(err.message || "Failed to save calculation to protocol.");
      console.error("Error saving calculation:", err);
    } finally {
      endOp(opKey);
    }
  };

  const handleClearAll = async () => {
    if (!currentUser) return;
    if (!tryBeginOp('clear-all')) return;
    try {
      // Subscription delivers the emptied stack + new recovery checkpoint.
      await clearAllProtocols(currentUser.uid, protocols);
      setError(null);
    } catch (err) {
      setError(err.message || "Failed to clear protocols.");
      console.error("Error clearing protocols:", err);
    } finally {
      endOp('clear-all');
    }
  };

  const handleRestoreCheckpoint = async (checkpoint) => {
    if (!currentUser) return;
    const opKey = `restore-${checkpoint?.id || 'unknown'}`;
    if (!tryBeginOp(opKey)) return;
    setLoading(true);
    try {
      // Subscription delivers the restored stack.
      await restoreFromCheckpoint(currentUser.uid, checkpoint);
      setError(null);
    } catch (err) {
      setError(err.message || "Failed to restore checkpoint.");
      console.error("Restore failed:", err);
    } finally {
      setLoading(false);
      endOp(opKey);
    }
  };

  const handleCreateCheckpoint = async (reason) => {
    if (!currentUser) return;
    if (!tryBeginOp('create-checkpoint')) return;
    try {
      // Subscription delivers the new checkpoint.
      await createCheckpoint(currentUser.uid, protocols, reason);
      setError(null);
    } catch (err) {
      setError(err.message || "Failed to create checkpoint.");
      console.error("Error creating checkpoint:", err);
    } finally {
      endOp('create-checkpoint');
    }
  };

  const handleDeleteCheckpoint = async (checkpointId) => {
    if (!currentUser) return;
    const opKey = `delete-checkpoint-${checkpointId}`;
    if (!tryBeginOp(opKey)) return;
    try {
      await deleteCheckpoint(currentUser.uid, checkpointId);
      setError(null);
    } catch (err) {
      setError(err.message || "Failed to delete checkpoint.");
      console.error("Error deleting checkpoint:", err);
    } finally {
      endOp(opKey);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 font-sans flex flex-col justify-between overflow-x-hidden">
      
      <div>
        {error && (
          <div className="bg-red-900/80 border-b border-red-700 px-4 py-3">
            <div className="max-w-7xl mx-auto flex items-start justify-between gap-4">
              <p className="text-sm text-red-100">{error}</p>
              <div className="flex items-center gap-3 shrink-0">
                <button
                  onClick={() => setRefreshKey(k => k + 1)}
                  className="text-sm font-semibold text-red-200 hover:text-white underline underline-offset-2 decoration-red-400/60 hover:decoration-white transition"
                  aria-label="Retry sync"
                >
                  Retry
                </button>
                <button
                  onClick={() => setError(null)}
                  className="text-red-200 hover:text-white text-sm"
                  aria-label="Dismiss error"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        )}

        <Navbar
          activeTab={activeTab}
          onSelectTab={handleSelectTab}
          onOpenAuth={() => setIsAuthOpen(true)}
          onOpenAddModal={handleOpenAddModal}
          onOpenHistoryModal={() => handleViewHistory(null)}
          onOpenReleaseNotes={() => setIsReleaseNotesOpen(true)}
          onTriggerSeed={handleSeedData}
        />

        {activeTab === 'stack' ? (
          currentUser ? (
            <Dashboard
              currentUser={currentUser}
              protocols={protocols}
              loading={loading}
              onEditProtocol={handleOpenEditModal}
              onDuplicateProtocol={handleOpenDuplicateModal}
              onStatusChange={handleStatusChange}
              onDeleteProtocol={handleDeleteProtocol}
              onViewHistory={handleViewHistory}
              onOpenAddModal={handleOpenAddModal}
              onSeedData={handleSeedData}
              checkpoints={checkpoints}
              onClearAll={handleClearAll}
              onRestoreCheckpoint={handleRestoreCheckpoint}
              onCreateCheckpoint={handleCreateCheckpoint}
              onDeleteCheckpoint={handleDeleteCheckpoint}
            />
          ) : (
            <LandingHero
              onOpenAuth={() => setIsAuthOpen(true)}
              onExploreTools={() => handleSelectTab('tools')}
              onExploreCalendar={() => handleSelectTab('calendar')}
            />
          )
        ) : activeTab === 'tools' ? (
          <ToolsHub
            protocols={protocols}
            onSaveToProtocol={handleSaveCalculatorToProtocol}
          />
        ) : activeTab === 'serum' ? (
          <SerumHub
            protocols={currentUser ? protocols : SEED_PROTOCOLS}
            auditLogs={allAuditLogs}
            onOpenAddModal={handleOpenAddModal}
            onSelectTab={handleSelectTab}
          />
        ) : (
          <CalendarHub
            protocols={currentUser ? protocols : SEED_PROTOCOLS}
            isDemo={!currentUser}
            onOpenAuth={() => setIsAuthOpen(true)}
            currentUser={currentUser}
          />
        )}
      </div>

      {/* Footer */}
      <footer className="py-6 border-t border-slate-900 text-center text-xs text-slate-500">
        <p className="flex items-center justify-center space-x-1.5 flex-wrap gap-y-1">
          <span>Supps &amp; Peps Stack Tracker</span>
          <span>•</span>
          <button
            type="button"
            onClick={() => setIsReleaseNotesOpen(true)}
            className="group inline-flex items-center space-x-1 text-slate-400 hover:text-sky-400 underline underline-offset-2 decoration-slate-700 hover:decoration-sky-400 transition cursor-pointer"
            title="View Release Notes & Version Updates"
            aria-label="View Release Notes"
          >
            <span>Version {APP_VERSION}</span>
            <span className="text-[10px] px-1.5 py-0.2 rounded bg-slate-800 text-slate-400 border border-slate-700/60 font-mono group-hover:border-sky-500/40 group-hover:text-sky-300 transition">
              Notes
            </span>
          </button>
        </p>
      </footer>

      {/* Modals */}
      <AuthModal
        isOpen={isAuthOpen}
        onClose={() => setIsAuthOpen(false)}
      />

      <ProtocolModal
        isOpen={isProtocolModalOpen}
        onClose={() => {
          setIsProtocolModalOpen(false);
          setIsDuplicate(false);
        }}
        onSave={handleSaveProtocol}
        protocolToEdit={protocolToEdit}
        isDuplicate={isDuplicate}
      />

      <HistoryLogModal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        logs={historyLogs}
        protocolName={historyProtocolName}
      />

      <OnboardingModal
        isOpen={isOnboardingOpen}
        onClose={handleCloseOnboarding}
        onLoadSampleTemplate={handleLoadSampleTemplate}
        onStartBlank={handleStartBlank}
        isSeeding={isSeedingTemplate}
      />

      <ReleaseNotesModal
        isOpen={isReleaseNotesOpen}
        onClose={() => setIsReleaseNotesOpen(false)}
      />

    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <MainApp />
    </AuthProvider>
  );
}
